import React, { useEffect, useState, useMemo } from "react";
import { Brain } from "lucide-react";
import {
  AreaChart, Area, XAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { RadialProgress, EmptyState } from "../ui";
import apiClient from "../../utils/apiClient";

/**
 * StudentAICard — AI Practice analytics card matching the UX reference.
 * Shows radial pass-rate, passed/failed counts, and a weekly trend area chart.
 *
 * Props:
 *   studentId — MongoDB student _id
 */
const StudentAICard = ({ studentId }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    apiClient
      .get("/ai/ai-attempts/stats", { params: { studentId } })
      .then((res) => setStats(res.data.stats || []))
      .catch(() => setStats([]))
      .finally(() => setLoading(false));
  }, [studentId]);

  // Aggregate totals
  const { total, passed, failed } = useMemo(() => {
    let p = 0, f = 0;
    stats.forEach((day) => {
      (day.attempts || []).forEach((a) => {
        if (a.score >= 50) p++;
        else f++;
      });
    });
    // Fallback: if attempts array is empty but count exists, use count
    if (p + f === 0) {
      stats.forEach((day) => {
        const avg = day.avgScore || 0;
        const cnt = day.count || 0;
        if (avg >= 50) p += cnt;
        else f += cnt;
      });
    }
    return { total: p + f, passed: p, failed: f };
  }, [stats]);

  // Weekly trend (last 6 weeks)
  const weeklyData = useMemo(() => {
    if (!stats.length) return [];
    const weeks = {};
    stats.forEach((day) => {
      const d = new Date(day._id);
      if (isNaN(d.getTime())) return; // skip unparseable _id values
      const weekStart = new Date(d);
      weekStart.setDate(d.getDate() - d.getDay() + 1);
      const key = weekStart.toISOString().slice(0, 10);
      if (!weeks[key]) weeks[key] = { week: "", attempts: 0, passed: 0 };
      (day.attempts || []).forEach((a) => {
        weeks[key].attempts++;
        if (a.score >= 50) weeks[key].passed++;
      });
    });
    const sorted = Object.entries(weeks)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([, v], i) => ({ ...v, week: `W${i + 1}` }));
    return sorted;
  }, [stats]);

  const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;

  if (loading) {
    return (
      <div className="ui-card sp-analytics-card">
        <div className="sp-analytics-gradient sp-analytics-gradient--violet">
          <div className="sp-analytics-header">
            <div className="sp-analytics-header-icon sp-analytics-header-icon--violet">
              <Brain size={18} color="#fff" />
            </div>
            <div>
              <h3 className="sp-analytics-header-title">AI Practice</h3>
              <p className="sp-analytics-header-sub">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="ui-card sp-analytics-card">
        <EmptyState
          icon={<Brain size={28} />}
          title="No AI Attempts Yet"
          description="Start practicing to see analytics"
        />
      </div>
    );
  }

  return (
    <div className="ui-card sp-analytics-card">
      <div className="sp-analytics-gradient sp-analytics-gradient--violet">
        <div className="sp-analytics-header">
          <div className="sp-analytics-header-icon sp-analytics-header-icon--violet">
              <Brain size={18} color="#fff" />
          </div>
          <div>
            <h3 className="sp-analytics-header-title">AI Practice</h3>
            <p className="sp-analytics-header-sub">{total} total attempts</p>
          </div>
        </div>

        <div className="sp-analytics-body">
          <RadialProgress value={passRate} color="#8b5cf6" label="Pass Rate" />
          <div className="sp-analytics-stats">
            <div className="sp-analytics-stat-row">
              <div className="sp-analytics-stat-label">
                <span className="sp-analytics-dot sp-analytics-dot--emerald" />
                Passed
              </div>
              <span className="sp-analytics-stat-value sp-analytics-stat-value--emerald">{passed}</span>
            </div>
            <div className="sp-analytics-stat-row">
              <div className="sp-analytics-stat-label">
                <span className="sp-analytics-dot sp-analytics-dot--rose" />
                Failed
              </div>
              <span className="sp-analytics-stat-value sp-analytics-stat-value--rose">{failed}</span>
            </div>
          </div>
        </div>
      </div>

      {weeklyData.length > 0 && (
        <div className="sp-analytics-chart">
          <p className="sp-analytics-chart-label">WEEKLY TREND</p>
          <div className="sp-analytics-chart-area">
            <ResponsiveContainer>
              <AreaChart data={weeklyData}>
                <defs>
                  <linearGradient id="aiPassGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 10, fill: "var(--text-tertiary)" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="passed"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#aiPassGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAICard;
